import { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  Alert,
  Box,
  useTheme,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import Refresh from "@mui/icons-material/Refresh";
import AddwardModal from "./Addwards.jsx";
import { getAllWards, deleteWard } from "../../services/WardService.js";
import { confirmAsync } from "../../utils/appNotify";
import { useAuth } from "../../context/AuthContext.jsx";
import EditIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { getBedsByWard } from "../../services/BedService.js";
import { getAll as getAllAdmissions } from "../../services/PatientAdmissionService.js";
import { getAssignmentByAdmissionId } from "../../services/PatientAssignmentService.js";
import { getPatientById } from "../../services/patientService.js";
import { getOrganizationName } from "../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import dayjs from "dayjs";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";

const columns = [
  { id: "id", label: "Id" },
  { id: "name", label: "Name" },
  { id: "floor", label: "Floor" },
  { id: "wardType", label: "Ward Type" },
  { id: "totalBeds", label: "Total Beds" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

function WardsTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [wards, setWards] = useState([]);
  const [fetching, setFetching] = useState({ ward_list: false });
  const [openWardModal, setOpenWardModal] = useState(false);
  const [wardInfo, setWardInfo] = useState(null);
  const theme = useTheme();
  const { user } = useAuth();
  const roleName = (user?.roleName || "").toLowerCase();
  const canDelete = roleName === "admin" || roleName === "super_admin" || roleName === "superadmin";
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterFloor, setFilterFloor] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [wardBedStats, setWardBedStats] = useState({});
  const [openOccupancyDialog, setOpenOccupancyDialog] = useState(false);
  const [selectedWard, setSelectedWard] = useState(null);
  const [occupiedBedDetails, setOccupiedBedDetails] = useState([]);
  const [loadingOccupiedDetails, setLoadingOccupiedDetails] = useState(false);

  useEffect(() => {
    fetchWardList();
  }, []);

  const fetchWardList = async () => {
    setFetching((prev) => ({ ...prev, ward_list: true }));
    try {
      const data = await getAllWards();
      console.log("Fetched wards:", data);
      const sorted = [...(data || [])].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      setWards(sorted);
    } catch (error) {
      console.error("Failed to fetch wards:", error);
    } finally {
      setFetching((prev) => ({ ...prev, ward_list: false }));
    }
  };

  const isBedOccupied = (value) =>
    value === true || value === "true" || value === 1 || value === "1";

  const fetchWardBedStats = async (wardsList) => {
    const statsByWard = {};
    await Promise.all(
      (wardsList || []).map(async (ward) => {
        try {
          const beds = await getBedsByWard(ward.id);
          const bedList = Array.isArray(beds) ? beds : [];
          const occupiedCount = bedList.filter((bed) => isBedOccupied(bed?.isOccupied)).length;
          statsByWard[ward.id] = {
            created: bedList.length,
            total: Number(ward?.totalBeds) || bedList.length || 0,
            occupied: occupiedCount,
            beds: bedList,
          };
        } catch (error) {
          statsByWard[ward.id] = {
            created: 0,
            total: Number(ward?.totalBeds) || 0,
            occupied: 0,
            beds: [],
          };
        }
      })
    );
    setWardBedStats(statsByWard);
  };

  useEffect(() => {
    if (wards.length > 0) {
      fetchWardBedStats(wards);
    } else {
      setWardBedStats({});
    }
  }, [wards]);

  const getPatientDisplayName = (patient) => {
    if (!patient) return null;
    return (
      patient.name ||
      [patient.firstName, patient.lastName].filter(Boolean).join(" ").trim() ||
      patient.patientName ||
      null
    );
  };

  const enrichPatientNames = async (rows) => {
    const patientIdsToFetch = [
      ...new Set(
        rows
          .map((r) => r.patientId)
          .filter((id) => id != null && id !== "-" && !Number.isNaN(Number(id)))
      ),
    ].filter((id) => {
      const row = rows.find((r) => String(r.patientId) === String(id));
      return !row?.patientName || row.patientName === "-";
    });

    if (patientIdsToFetch.length === 0) return rows;

    const nameByPatientId = {};
    await Promise.all(
      patientIdsToFetch.map(async (patientId) => {
        try {
          const patient = await getPatientById(patientId);
          const name = getPatientDisplayName(patient);
          if (name) nameByPatientId[patientId] = name;
        } catch (error) {
          // Keep row without name if patient lookup fails.
        }
      })
    );

    return rows.map((row) => ({
      ...row,
      patientName:
        row.patientName && row.patientName !== "-"
          ? row.patientName
          : nameByPatientId[row.patientId] || row.patientName || "-",
    }));
  };

  const handleViewWardOccupancy = async (ward) => {
    setSelectedWard(ward);
    setOpenOccupancyDialog(true);
    setLoadingOccupiedDetails(true);
    setOccupiedBedDetails([]);
    try {
      const wardBeds = wardBedStats[ward.id]?.beds || (await getBedsByWard(ward.id)) || [];
      const occupiedBeds = (Array.isArray(wardBeds) ? wardBeds : []).filter((bed) =>
        isBedOccupied(bed?.isOccupied)
      );
      const occupiedBedIds = new Set(occupiedBeds.map((bed) => Number(bed.id)).filter(Boolean));
      if (occupiedBedIds.size === 0) {
        setOccupiedBedDetails([]);
        return;
      }

      const admissionsData = await getAllAdmissions();
      const admittedAdmissions = (Array.isArray(admissionsData) ? admissionsData : []).filter(
        (a) => String(a?.status || "").toLowerCase() === "admit"
      );

      const rows = [];
      for (const admission of admittedAdmissions) {
        try {
          const assignmentRes = await getAssignmentByAdmissionId(admission.id);
          const assignment = Array.isArray(assignmentRes) ? assignmentRes[0] : assignmentRes;
          if (!assignment) continue;

          const assignedWardId = Number(assignment?.wardId);
          const assignedBedId = Number(assignment?.bedId);
          if (assignedWardId !== Number(ward.id)) continue;
          if (!occupiedBedIds.has(assignedBedId)) continue;

          const bedInfo = occupiedBeds.find((b) => Number(b.id) === assignedBedId);
          const patientName =
            admission?.patientName ||
            assignment?.patientName ||
            null;

          rows.push({
            bedId: assignedBedId,
            bedNumber: bedInfo?.bedNumber ?? "-",
            patientName,
            patientId: admission?.patientId ?? "-",
            admissionId: admission?.id ?? "-",
          });
        } catch (error) {
          // Ignore per-admission assignment failures to keep dialog responsive.
        }
      }

      const uniqueRows = Array.from(
        new Map(rows.map((r) => [String(r.bedId), r])).values()
      ).sort((a, b) => String(a.bedNumber).localeCompare(String(b.bedNumber)));

      const rowsWithNames = await enrichPatientNames(uniqueRows);
      setOccupiedBedDetails(rowsWithNames);
    } catch (error) {
      console.error("Failed to fetch ward occupancy details:", error);
      setOccupiedBedDetails([]);
    } finally {
      setLoadingOccupiedDetails(false);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (ward) => {
    setWardInfo(ward);
    setOpenWardModal(true);
  };

  const handleDeleteWard = async (ward) => {
    const stats = wardBedStats[ward.id];
    const occupiedCount = stats?.occupied ?? 0;
    const createdCount = stats?.created ?? 0;

    if (occupiedCount > 0) {
      window.alert(
        `Cannot delete ward "${ward.name}". ${occupiedCount} bed(s) are currently occupied.`
      );
      return;
    }

    const confirmMessage =
      createdCount > 0
        ? `Delete ward "${ward.name}"? This ward has ${createdCount} bed(s) that may also be removed.`
        : `Are you sure you want to delete ward "${ward.name}"?`;

    const confirmed = await confirmAsync(confirmMessage, {
      title: "Delete ward",
      tone: "error",
      confirmLabel: "Delete",
    });
    if (!confirmed) return;

    try {
      await deleteWard(ward.id);
      await fetchWardList();
    } catch (error) {
      console.error("Failed to delete ward:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete ward. Please try again.";
      window.alert(message);
    }
  };

  const handleAddNew = () => {
    setWardInfo(null);
    setOpenWardModal(true);
  };

  const filteredRows = wards.filter((w) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vals = [w.id, w.name, w.floor, w.wardType, w.totalBeds]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (filterName.trim()) {
      if (!String(w.name || "").toLowerCase().includes(filterName.toLowerCase())) return false;
    }
    if (filterType) {
      if (String(w.wardType || "") !== filterType) return false;
    }
    if (filterFloor) {
      if (String(w.floor || "") !== filterFloor) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterType("");
    setFilterFloor("");
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Id", "Name", "Floor", "Ward Type", "Total Beds"];
    const tableRows = rows.map((r) => [r.id, r.name, r.floor, r.wardType, r.totalBeds]);

    await downloadStyledTableExcel({
      filename: `wards_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Wards",
      title: "Wards",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={TABLE_ADD_BUTTON_SX}
          onClick={handleAddNew}
        >
          Add Ward
        </Button>
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={fetchWardList}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField size="small" label="Name" value={filterName} onChange={(e)=>setFilterName(e.target.value)} />
            <TextField size="small" label="Ward Type" value={filterType} onChange={(e)=>setFilterType(e.target.value)} select sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {[...new Set(wards.map((w)=>w.wardType).filter(Boolean))].map((t)=> (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Floor" value={filterFloor} onChange={(e)=>setFilterFloor(e.target.value)} select sx={{ minWidth: 120 }}>
              <MenuItem value="">All</MenuItem>
              {[...new Set(wards.map((w)=>w.floor).filter(Boolean))].map((f)=> (
                <MenuItem key={f} value={String(f)}>{f}</MenuItem>
              ))}
            </TextField>
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell key={column.id}>
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching.ward_list ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : wards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Alert severity="info">No Wards Available</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.id}>
                      {columns.map((column) => (
                        <TableCell key={column.id}>
                          {row[column.id]}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Tooltip title="Edit Ward">
                            <IconButton
                              size="small"
                              sx={TABLE_ICON_BUTTON_SX}
                              onClick={() => handleEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View ward bed report">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon fontSize="small" />}
                              onClick={() => handleViewWardOccupancy(row)}
                            >
                              View {(wardBedStats[row.id]?.created ?? 0)}/{((wardBedStats[row.id]?.total ?? Number(row.totalBeds)) || 0)} | Occ {(wardBedStats[row.id]?.occupied ?? 0)}
                            </Button>
                          </Tooltip>
                          {canDelete && (
                            <Tooltip title="Delete Ward">
                              <IconButton
                                onClick={() => handleDeleteWard(row)}
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <AddwardModal
        open={openWardModal}
        onClose={async () => {
          setOpenWardModal(false);
          setWardInfo(null);
          await fetchWardList();
        }}
        wardInfo={wardInfo}
      />

      <Dialog
        open={openOccupancyDialog}
        onClose={() => setOpenOccupancyDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Ward Occupancy Details
          {selectedWard ? ` - ${selectedWard.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {selectedWard && (
            <Box mb={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2">Beds Created:</Typography>
              <Chip
                size="small"
                color="primary"
                label={`${wardBedStats[selectedWard.id]?.created ?? 0}/${
                  ((wardBedStats[selectedWard.id]?.total ?? Number(selectedWard.totalBeds)) || 0)
                }`}
              />
              <Typography variant="body2">Occupied Beds:</Typography>
              <Chip
                size="small"
                color="secondary"
                label={`${wardBedStats[selectedWard.id]?.occupied ?? 0}`}
              />
            </Box>
          )}

          {loadingOccupiedDetails ? (
            <Box py={2} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : occupiedBedDetails.length === 0 ? (
            <Alert severity="info">No occupied beds found for this ward.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Bed Number</StyledTableCell>
                    <StyledTableCell>Patient Name</StyledTableCell>
                    <StyledTableCell>Patient ID</StyledTableCell>
                    <StyledTableCell>Admission ID</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {occupiedBedDetails.map((item) => (
                    <TableRow key={`${item.bedId}-${item.admissionId}`}>
                      <TableCell>{item.bedNumber}</TableCell>
                      <TableCell>{item.patientName}</TableCell>
                      <TableCell>{item.patientId}</TableCell>
                      <TableCell>{item.admissionId}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOccupancyDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default WardsTable;